import { useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Roles } from "@/constants/roles";
import { getPrimaryRole, hasRole } from "@/utils/roleUtils";
import { PageHead } from "@/components/PageHead";
import EnvironmentBadge from "@/components/EnvironmentBadge";
import fsvLogo from "@/images/achieveos-logo.png";
import fsvLogoDark from "@/images/achieveos-logo-dark.png";
import { AchieverGrowthForm } from "./components/AchieverGrowthForm";
import { AdvisorPreferenceForm } from "./components/AdvisorPreferenceForm";
import { MarketingPanel } from "./components/MarketingPanel";
import { StepProgress } from "@/components/StepProgress";
import { profileStepCompleted } from "@/utils/tracking";
import { initSessionReplay } from "@/utils/amplitude";
import {
  ACHIEVER_STEP3_PANEL_CONTENT,
  ADVISOR_STEP3_PANEL_CONTENT,
} from "@/constants/signupPanelContent";
import { ThemeToggle } from "@/components/ThemeToggle";
import { getSignupStepLabels } from "@/constants/signupSteps";

export const SignUpStepThree = () => {
  const { user } = useAuth();

  const primaryRole = user ? getPrimaryRole(user) : null;

  useEffect(() => {
    initSessionReplay();
    if (primaryRole) {
      profileStepCompleted({ step_name: "preferences_viewed", user_role: primaryRole });
    }
  }, [primaryRole]);

  // hasRole() rather than getPrimaryRole(): getPrimaryRole collapses a
  // multi-role user to whichever role Array.find happens to hit first, so a
  // dual-role (or lead) user could match neither branch below and get a blank
  // card with no way forward. Advisor is checked first so a dual-role user
  // sees the advisor preferences form.
  const isAdvisor = hasRole(user, Roles.advisor);

  const renderForm = () => {
    if (!user) return null;
    if (isAdvisor) return <AdvisorPreferenceForm />;
    // Achiever is the default: every non-advisor signup walks the growth flow,
    // so an unexpected role value still gets a usable, completable page.
    return <AchieverGrowthForm />;
  };

  return (
    <>
      <PageHead
        title={isAdvisor ? "Expert Sign Up: Operator DNA" : "Achiever Sign Up: Growth"}
        description="Create your AchieveOS account"
      />
      <EnvironmentBadge />

      <div className="fixed inset-0 flex flex-col overflow-hidden font-inter bg-signup-form-bg bg-surface-page-bg">
        <div className="flex flex-1 min-h-0">
          {/* Left: form area */}
          <div className="flex-1 bg-signup-form-bg bg-surface-page-bg overflow-y-auto">
            <div className="flex flex-col items-center px-4 md:px-8 min-h-full py-8">
              <div className="w-full max-w-[620px] flex flex-col">
                {/* Logo + Theme Toggle */}
                <div className="flex items-center justify-between mb-10">
                  <img
                    src={fsvLogo}
                    alt="AchieveOS"
                    className="logo-light-mode h-[50px] w-[218px] max-w-full object-contain"
                  />
                  <img
                    src={fsvLogoDark}
                    alt="AchieveOS"
                    className="logo-dark-mode h-[50px] w-[218px] max-w-full object-contain"
                  />
                  <ThemeToggle />
                </div>

                {/* Last step for achievers; Experts continue to the S.T.A.R. Portfolio. */}
                <StepProgress
                  currentStep={3}
                  totalSteps={getSignupStepLabels(isAdvisor).length}
                  stepLabels={getSignupStepLabels(isAdvisor)}
                />

                {/* Form card */}
                <div className="bg-theme-bg-surface border border-theme-border rounded-2xl px-4 md:px-10 pt-10 pb-10 mt-8">
                  {renderForm()}
                </div>
              </div>
            </div>
          </div>

          {/* Right: marketing panel */}
          <div className="hidden md:block">
            <MarketingPanel
              content={isAdvisor ? ADVISOR_STEP3_PANEL_CONTENT : ACHIEVER_STEP3_PANEL_CONTENT}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default SignUpStepThree;
